import '@fontsource-variable/dm-sans';
import '@fontsource-variable/open-sans';
import 'cal-heatmap/cal-heatmap.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import './index.css';

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import { lazy, StrictMode, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import ReactLazyLoading from '@components/ReactLazyLoading.tsx';
import { APP_NAME } from '@const';
import { Box, CssBaseline, ThemeProvider } from '@mui/material';
import { LicenseInfo } from '@mui/x-license';

import Admin from './admin/Admin.tsx';
import AppAuth from './AppAuth.tsx';
import AuthProvider from './AuthProvider.tsx';
import HelmetWrapper from './HelmetWrapper.tsx';
import InvalidUrlRedirect from './invalidUrlRedirect.tsx';
import MaintenanceBanner from './MaintenanceBanner.tsx';
import { store } from './store.ts';
import theme from './theme.ts';

const { VITE_BASE_NAME } = import.meta.env;

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(isoWeek);
dayjs.extend(weekday);
dayjs.extend(isSameOrBefore);

const AppView = lazy(() => import('./AppView.tsx'));
const Stats = lazy(() => import('./Views/Stats/'));

// Shared layout component for AppView routes
const AppViewLayout = () => (
  <HelmetWrapper title={APP_NAME}>
    <Suspense fallback={<ReactLazyLoading />}>
      <AppView />
    </Suspense>
  </HelmetWrapper>
);

LicenseInfo.setLicenseKey('62c2fd71b6059427f520d91dd1d6db36Tz0xMjY3MTgsRT0xODA4MTc5MTk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMy0yMDI0LEtWPTI=');

ReactDOM.createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Provider store={store}>
      <HelmetProvider>
        <AuthProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <ToastContainer position="top-center" />
            <Box
              sx={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                minHeight: 0,
                minWidth: 0
              }}
            >
              <MaintenanceBanner />
              <BrowserRouter basename={VITE_BASE_NAME}>
                <AppAuth>
                  <Routes>
                    <Route path="/" element={<AppViewLayout />} />
                    <Route path="/campaign/:campaign_id" element={<AppViewLayout />} />
                    <Route path="/view/:view_id" element={<AppViewLayout />} />
                    <Route path="/tito" element={<AppViewLayout />} />
                    <Route
                      path="/stats"
                      element={
                        <HelmetWrapper title={APP_NAME + ': Stats'}>
                          <Suspense fallback={<ReactLazyLoading />}>
                            <Stats />
                          </Suspense>
                        </HelmetWrapper>
                      }
                    />
                    <Route path="/admin/*" element={<Admin />} />
                    <Route path="*" element={<InvalidUrlRedirect />} />
                  </Routes>
                </AppAuth>
              </BrowserRouter>
            </Box>
          </ThemeProvider>
        </AuthProvider>
      </HelmetProvider>
    </Provider>
  </StrictMode>
);
